/**
 * Lesson attachments — shared client helpers.
 *
 * Mirrors backend/services/lesson_attachments.py. The caps here are a UX
 * courtesy so a doomed upload never travels; the server re-checks everything
 * against S3's measured size and stays authoritative.
 *
 * Why 10 MB: the attachments are emailed with the lesson summary, base64
 * inflates them ~37% on the wire, and iCloud Mail only accepts 20 MB inbound.
 * 10 MB of files is ~13.7 MB encoded, which clears every major provider.
 */

export const MAX_ATTACHMENTS_PER_LESSON = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS_TOTAL_BYTES = 10 * 1024 * 1024;

// MIME type → canonical extension. Must stay in sync with the backend's
// ATTACHMENT_TYPES, which is the real allowlist.
const ATTACHMENT_TYPES = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heic",
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/mp4": "m4a",
  "audio/x-m4a": "m4a",
  "audio/m4a": "m4a",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/wave": "wav",
};

// Extension → MIME type, for files whose `File.type` comes back empty.
//
// This is not a theoretical edge case: iOS Safari reports an empty `File.type`
// for some files picked out of the Files app, and instructors upload from
// their phone. Without this fallback every one of those attachments would be
// rejected client-side with a type error the file doesn't deserve.
const EXTENSION_TYPES = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heic",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
};

// Both MIME types and bare extensions: browsers differ in which they honour,
// and iOS in particular is happier with extensions. Same belt-and-braces as
// the lesson-audio picker in HansRecording.
export const ATTACHMENT_ACCEPT = [
  ...Object.keys(ATTACHMENT_TYPES),
  ...Object.keys(EXTENSION_TYPES).map((ext) => `.${ext}`),
].join(",");

export const ATTACHMENT_HINT =
  "PDF, image or audio · up to 5 files, 10 MB total";

/**
 * Resolve the MIME type of a picked attachment, or "" when it isn't allowed.
 * Prefers the browser-reported type and falls back to the extension.
 *
 * @param {File} file
 * @returns {string}
 */
export function resolveAttachmentType(file) {
  const type = (file?.type || "").split(";")[0].trim().toLowerCase();
  if (ATTACHMENT_TYPES[type]) return type;
  if (type) return ""; // a real but disallowed type — don't guess past it
  const ext = (file?.name || "").split(".").pop()?.toLowerCase();
  return EXTENSION_TYPES[ext] || "";
}

/**
 * Human-readable byte size. Whole numbers under 1 MB, one decimal above, so a
 * running "5.9 MB of 10 MB used" total reads cleanly.
 *
 * @param {number} bytes
 * @returns {string}
 */
export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  // Drop a trailing ".0" so the cap renders as "10 MB", matching the hint copy
  // and the number in every error message.
  return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
}

/**
 * Total bytes currently attached to a lesson.
 *
 * @param {Array<{size_bytes?: number}>} attachments
 * @returns {number}
 */
export function totalAttachmentBytes(attachments) {
  return (attachments || []).reduce((sum, a) => sum + (Number(a?.size_bytes) || 0), 0);
}

/**
 * Validate a picked file against the caps, given what's already attached.
 * Returns an error message, or "" when the file is acceptable.
 *
 * @param {File} file
 * @param {Array} attachments
 * @returns {string}
 */
export function validateAttachment(file, attachments) {
  if (!resolveAttachmentType(file)) {
    return "Attach a PDF, an image (JPEG, PNG, WebP, HEIC) or audio (MP3, M4A, WAV).";
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `Files must be under ${formatBytes(MAX_ATTACHMENT_BYTES)}.`;
  }
  if ((attachments || []).length >= MAX_ATTACHMENTS_PER_LESSON) {
    return `A lesson can have at most ${MAX_ATTACHMENTS_PER_LESSON} attachments.`;
  }
  if (totalAttachmentBytes(attachments) + file.size > MAX_ATTACHMENTS_TOTAL_BYTES) {
    return `That would go over the ${formatBytes(MAX_ATTACHMENTS_TOTAL_BYTES)} limit for one lesson.`;
  }
  return "";
}
